import LayoutHeader from 'components/LayoutHeader';
import { useTranslation } from 'react-i18next';
import GenericLibrary from './components/GenericLibrary';
import { ThemeLibraryColumnDefinitions } from './ThemeLibraryColumnDefinitions';
import {
  ActionsProps,
  AddButton,
  ConfirmModal,
  HeaderMenuBreadcrumb,
  POSLoadingOverlay,
  showNotification,
  SortDirection,
  SortSelector,
  SortState,
  useHttp,
} from '@publicisgroupe/p-os-components';
import { ThemeDetails } from 'interfaces/ThemeDetails';
import { useCallback, useEffect, useState } from 'react';
import { useGetClientProfiles } from './hooks/useGetClientProfiles';
import { TableState } from './components/GenericLibrary/types';
import { useHistory } from 'react-router-dom';
import { ROUTE_FRAME_BRAND_CUSTOMIZATION, ROUTE_MODULE_CONFIG } from 'utils/routes';
import { PaginationProps } from 'semantic-ui-react';
import { constructSortState } from './components/GenericLibrary/helpers';
import _ from 'lodash';
import { webframeApi } from 'api/webframeApi';
import useErrorNotification from 'hooks/useErrorNotification';

const defaultSortState: SortState<ThemeDetails, any> = {
  name: 'Created On',
  accessor: 'CreatedAt',
  selector: (file) => file.createdAt,
  direction: SortDirection.Descending,
};

const defaultTableState: TableState<ThemeDetails> = {
  pageSize: 10,
  data: [],
  pageIndex: 0,
  sortState: defaultSortState,
  globalFilters: [],
};

export const ThemeLibraryPage = () => {
  const [pageCount, setPageCount] = useState<number>(1);
  const [tableState, setTableState] = useState<TableState<ThemeDetails>>(defaultTableState);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [clientProfileToDelete, setClientProfileToDelete] = useState<string>('');

  const { showErrorNotification } = useErrorNotification();
  const { t } = useTranslation();
  const history = useHistory();
  const getClientProfiles = useGetClientProfiles();
  const http = useHttp();

  const columnDefinitions = ThemeLibraryColumnDefinitions();

  const getClientProfilesData = useCallback(async () => {
    setIsLoading(true);

    try {
      const skip = tableState.pageIndex * tableState.pageSize;
      const url = `/client-profiles?Paging.Skip=${skip}&Paging.Take=${tableState.pageSize}&Sorting.Field=${tableState.sortState.accessor}&Sorting.Direction=${tableState.sortState.direction}`;

      const themesData = await getClientProfiles(url);

      if (themesData.items.length === 0) {
        setPageCount(1);
      } else {
        setPageCount(Math.ceil(themesData.totalCount / tableState.pageSize));
      }
      setTableState((state) => ({
        ...state,
        data: themesData.items,
      }));
    } catch (e) {
      showErrorNotification({ content: t('ClientProfilesLoadErrorMessage') });
    } finally {
      setIsLoading(false);
    }
  }, [
    getClientProfiles,
    showErrorNotification,
    t,
    tableState.pageIndex,
    tableState.pageSize,
    tableState.sortState.accessor,
    tableState.sortState.direction,
  ]);

  useEffect(() => {
    void getClientProfilesData();
  }, [getClientProfilesData]);

  const handleAdd = useCallback(() => {
    history.push(ROUTE_FRAME_BRAND_CUSTOMIZATION);
  }, [history]);

  const handleEdit = useCallback(
    (clientName: string, clientProfileId: string) => {
      history.push(ROUTE_FRAME_BRAND_CUSTOMIZATION, {
        clientName: clientName,
        clientProfileId: clientProfileId,
        isEditMode: true,
      });
    },
    [history]
  );

  const deleteProfile = async () => {
    setIsLoading(true);
    setDeleteModalOpen(false);

    try {
      await webframeApi(http)?.deleteClientProfile(clientProfileToDelete);
      await getClientProfilesData();

      showNotification.success({
        title: t('SuccessfulDeleteMessage'),
        content: t('ClientProfileDeleteSuccessMessage'),
        inverted: true,
      });
    } catch (e: any) {
      showErrorNotification({ content: t('ClientProfileDeleteError') });
    } finally {
      setIsLoading(false);
      setClientProfileToDelete('');
    }
  };

  const handleDeleteProfile = (profileId: string) => {
    setClientProfileToDelete(profileId);
    setDeleteModalOpen(true);
  };

  const actions: ActionsProps<ThemeDetails> = {
    orientation: 'vertical',
    actions: [],
    menu: {
      position: 'left center',
      menuItems: [
        {
          icon: 'edit',
          name: 'Edit',
          onClick: (item: ThemeDetails) => handleEdit(item.client, item.id),
        },
        {
          icon: 'delete',
          name: 'Delete',
          onClick: (item: ThemeDetails) => handleDeleteProfile(item.id),
        },
      ],
    },
  };

  const breadcrumbs = [
    {
      name: t('Config'),
      onClick: () => history.push(ROUTE_MODULE_CONFIG),
    },
  ] as HeaderMenuBreadcrumb[];

  const handlePageChange = useCallback(
    (data: PaginationProps) => {
      const newPageIndex = (data.activePage as number) - 1;
      setTableState({
        ...tableState,
        pageIndex: newPageIndex,
      });
    },
    [tableState]
  );

  const handleSort = (selector: SortSelector<ThemeDetails, any>) => {
    const newSortState = constructSortState(
      selector,
      columnDefinitions,
      defaultSortState,
      tableState.sortState
    );

    if (_.isEmpty(newSortState)) return;

    setTableState({
      ...tableState,
      sortState: newSortState as SortState<ThemeDetails, any>,
      pageIndex: 0,
    });
  };

  return (
    <POSLoadingOverlay loading={isLoading}>
      <div className="theme-library-page">
        <LayoutHeader breadcrumbs={breadcrumbs} title={t('Themes')} />

        <main>
          <div className="theme-library-page-content">
            <div className="actions">
              <AddButton onClick={handleAdd}>{t('AddData')}</AddButton>
            </div>
            <GenericLibrary
              genericTableProps={{
                columnDefinitions,
                noDataFoundMessage: t('TableSearchErrorMessage'),
                actions,
                pageCount,
              }}
              tableState={tableState}
              onPageChange={handlePageChange}
              onSort={handleSort}
              pageCount={pageCount}
            />
            <ConfirmModal
              openConfirmModal={deleteModalOpen}
              headerContent={t('ClientProfileDeleteConfirmation')}
              subHeaderContent={''}
              primaryButtonText={t('Delete')}
              secondaryButtonText={t('Cancel')}
              setOpenConfirmModal={setDeleteModalOpen}
              onConfirm={() => void deleteProfile()}
            />
          </div>
        </main>
      </div>
    </POSLoadingOverlay>
  );
};
